import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { useCallback, useContext, useState } from 'react';
import { TEST_GROUP_PATTERN } from 'src/hooks/useOptimizeNext';
import { useShopSelector } from 'src/hooks/useShopSelector';
import { fetchAvailableProducts } from 'src/libs/api/productsApi';
import { fetchSearchProducts } from 'src/libs/api/searchApi';
import { postVaisrSearchProducts } from 'src/libs/api/vairsApi';
import type { User } from 'src/libs/wa/models/entities/User';
import type { ProductDetail } from 'src/models/product/schema';
import { AuthContext } from 'src/stores/Auth';
import type { LOCALE_TYPE } from 'src/types/locale';
import { getCookieValue } from 'src/utils/createDataLayerObject';
import { isLocaleTw } from 'src/utils/locale';
import { mixpanel } from 'src/utils/mixpanel';
import useSWR from 'swr';

const proxyPurchaseProductIds = [15564, 15565, 15566, 15567, 15568, 15569, 15570, 15571];

const filterExcludedProducts = (products: ProductDetail[] | undefined): ProductDetail[] => {
  return products?.filter((product) => !proxyPurchaseProductIds.includes(product.productId)) ?? [];
};

const generateParams = (query: string, pageIndex: number, user: User | null | undefined) => ({
  q: decodeURIComponent(query),
  visitorId: getCookieValue('_ga'),
  page: pageIndex,
  ...(user ? { userInfo: { userId: user.id.toString() } } : {})
});

const useSearchProducts = (
  query: string | undefined,
  optimizeNextPattern: number | undefined,
  pageIndex: number
): {
  data: ProductDetail[] | undefined;
  isLoading: boolean;
  totalPageCount: number | undefined;
  attributionToken: string;
} => {
  const { currentShop } = useShopSelector();

  const { user } = useContext(AuthContext);
  const { locale } = useRouter();
  const currentShopId = currentShop?.id;
  const [totalPageCount, setTotalPageCount] = useState<number | undefined>(undefined);
  const [attributionToken, setAttributionToken] = useState<string>('');

  const fetcher = useCallback(async (): Promise<ProductDetail[] | undefined> => {
    // 初期レンダーではqueryがundefinedで入るため何もしない
    if (!query) return;

    if (isLocaleTw(locale as LOCALE_TYPE) && optimizeNextPattern === undefined) return;

    if (optimizeNextPattern === TEST_GROUP_PATTERN) {
      const params = generateParams(query, pageIndex, user);
      const { searchResults, attributionToken, totalPageCount } = await postVaisrSearchProducts(params);
      setTotalPageCount(totalPageCount);
      setAttributionToken(attributionToken);

      const orderableProductIds = searchResults
        .filter((product) => product.orderable)
        .map((product) => product.productId);
      const { productAvailabilities } = await fetchAvailableProducts({ ids: orderableProductIds });
      const availableProducts = searchResults.filter((product) =>
        productAvailabilities.some(
          (availability) => availability.productId === product.productId && availability.orderable
        )
      );
      return availableProducts;
    } else {
      const res = await fetchSearchProducts(query, currentShopId);
      const decodedQuery = decodeURIComponent(query);
      mixpanel.onSearchButtonTapped(
        decodedQuery,
        decodedQuery.split(/(\s)/).filter((query) => query !== ' ')
      );

      // 検索結果がない場合はエラーを吐くようにする
      if (res.isLeft()) {
        Sentry.captureException(res.value.errors.join(','));
        return;
      }
      return res.value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId, optimizeNextPattern, query, pageIndex]);

  const key =
    optimizeNextPattern === TEST_GROUP_PATTERN
      ? `/api/vaisrSearch?q=${query}&page=${pageIndex}`
      : `/api/search?q=${query}&shopId=${currentShopId}`;
  const { data, isLoading } = useSWR(key, fetcher, {
    errorRetryCount: 0,
    // TEST_GROUP_PATTERNの場合、最新のtokenを取得するため、キャッシュは行いません
    dedupingInterval: optimizeNextPattern === TEST_GROUP_PATTERN ? 0 : 3600000 // 1時間,
  });

  return {
    data: filterExcludedProducts(data),
    isLoading,
    totalPageCount,
    attributionToken
  };
};

export { useSearchProducts };
